import React, { useContext, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import Typography from 'kingpin/atoms/Typography';
import PopUpMenu from 'kingpin/atoms/PopUpMenu';
import appRoutes from 'navigation/appRoutes';
import LeftNavItem from '../../../Common/LeftNavItem';
import NavMenuPopup from './NavMenuPopup';
import PermissionGates from '../../../../../../components/PermissionGates';
import PERMISSIONS from '../../../../../../permissionDefinitions';
import useContent from '../../../../../../screens/V3/ContentIndex/useContent';
import RolesContext from '../../../../../../contexts/RolesContext';
import PopUpMenuChevron from './PopUpMenuChevron';

export const ContentLink = ({ to, label }: { to: string; label: string }) => {
  return (
    <NavLink to={to}>
      <NavMenuPopup.ItemDiv>
        <Typography.Body type={'Label 14'}>{label}</Typography.Body>
      </NavMenuPopup.ItemDiv>
    </NavLink>
  );
};

const EXTRA_CONTENT_PERMISSIONS = [
  PERMISSIONS.DATA_MANAGEMENT.GADGETS,
  PERMISSIONS.DATA_MANAGEMENT.DATA_MANAGER_ACCESS,
];

const CREATE_CONTENT_PERMISSIONS = [
  PERMISSIONS.DASHBOARDS.CREATE,
  PERMISSIONS.REPORT_MANAGEMENT.CREATE,
  PERMISSIONS.SCORECARDS.CRUD_SCORECARD,
  PERMISSIONS.GOALS.CRUD_GOALS,
];

const ContentMenu = () => {
  const { currentPermissions } = useContext(RolesContext);
  const content = useContent();
  const { pathname } = useLocation();
  const isSelected = useMemo(() => {
    return (
      pathname.includes('/data-manager') ||
      pathname.includes('/wallboard') ||
      pathname.includes('/content') ||
      pathname.includes(appRoutes.loggedIn.studio)
    );
  }, [pathname]);

  const hasContentLink = useMemo(() => {
    if (content.length > 0) {
      return true;
    }
    return CREATE_CONTENT_PERMISSIONS.some((p) =>
      currentPermissions.includes(p),
    );
  }, [content.length, currentPermissions]);

  const hasMenu = useMemo(() => {
    if (hasContentLink) {
      return true;
    }

    if (EXTRA_CONTENT_PERMISSIONS.some((p) => currentPermissions.includes(p))) {
      return true;
    }

    if (
      currentPermissions.includes(PERMISSIONS.USER_MANAGEMENT.CREATE_WALLBOARD)
    ) {
      return true;
    }

    return false;
  }, [currentPermissions, hasContentLink]);

  if (!hasMenu) {
    return null;
  }

  return (
    <PopUpMenu
      trigger={({ isOpen }: { isOpen: boolean }) => (
        <LeftNavItem isOpen={isOpen || isSelected}>
          <Typography.Body type={'Label 14'}>Content</Typography.Body>
          <PopUpMenuChevron isOpen={isOpen} />
        </LeftNavItem>
      )}
      children={() => (
        <NavMenuPopup.PopupDiv>
          {hasContentLink && (
            <ContentLink to={'/content'} label={'View All Content'} />
          )}
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.USER_MANAGEMENT.CREATE_WALLBOARD}
          >
            <ContentLink to={'/wallboards'} label={'Wallboards'} />
          </PermissionGates.Has>
          <PermissionGates.HasAny
            requiredPermissions={EXTRA_CONTENT_PERMISSIONS}
          >
            <NavMenuPopup.Divider />
            <PermissionGates.Has
              requiredPermission={
                PERMISSIONS.DATA_MANAGEMENT.DATA_MANAGER_ACCESS
              }
            >
              <ContentLink to={'/data-manager'} label={'Data Manager'} />
            </PermissionGates.Has>
          </PermissionGates.HasAny>
        </NavMenuPopup.PopupDiv>
      )}
      position={'bottom-start'}
    />
  );
};

export default ContentMenu;
